import {
  ToastOptions as StatelyToastOptions,
  ToastQueue as StatelyToastQueue,
  useToastQueue,
} from "@react-stately/toast";
import { createPortal } from "react-dom";

import { IToastState, Toast } from "./Toast";
import { ToastPosition, ToastVariant, type ToastPositionValue } from "./enums";

export interface IToastOptions extends Omit<StatelyToastOptions, "priority"> {
  position?: ToastPositionValue;
}

type CloseFunction = () => void;

let globalToastQueue: StatelyToastQueue<IToastState> | null = null;

function getGlobalToastQueue() {
  if (!globalToastQueue) {
    globalToastQueue = new StatelyToastQueue({
      maxVisibleToasts: 1,
    });
  }

  return globalToastQueue;
}

export const ToastProvider = () => {
  let state = useToastQueue(getGlobalToastQueue());

  return state.visibleToasts.length > 0 ? (
    createPortal(
      <div className="relative flex flex-col">
        {state.visibleToasts.map((toast) => (
          <Toast key={toast.key} toast={toast} state={state} />
        ))}
      </div>,
      document.body,
    )
  ) : (
    <></>
  );
};

function addToast(
  children: string,
  variant: IToastState["variant"],
  options: IToastOptions = {},
): CloseFunction | undefined {
  let value = {
    children,
    variant,
    position: (options.position as ToastPosition) ?? ToastPosition.Top,
    timeout: options.timeout || 2000,
  };

  let timeout = options.timeout || 2000;

  let queue = getGlobalToastQueue();
  let key = queue.add(value, { timeout, onClose: options.onClose });

  return (() => queue.close(key)) as CloseFunction;
}

export const ToastQueue = {
  success(
    children: string,
    options: IToastOptions = {},
  ): CloseFunction | undefined {
    return addToast(children, ToastVariant.Success, options);
  },
  warning(
    children: string,
    options: IToastOptions = {},
  ): CloseFunction | undefined {
    return addToast(children, ToastVariant.Warning, options);
  },
  danger(
    children: string,
    options: IToastOptions = {},
  ): CloseFunction | undefined {
    return addToast(children, ToastVariant.Danger, options);
  },
};
